import {
  Button,
  Card,
  CardContent,
  Typography,
  CardMedia,
  Grid,
  IconButton,
  Dialog,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AdminPage.module.css";
import { Box } from "@mui/system";
import animatedAvatarUrl from "../../../images/man.png";
import {
  useLoaderContext,
  useToastContext,
} from "../../../Context/ToastContext";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import EditIcon from "@mui/icons-material/Edit";
import { useUserContext } from "../../../Context/UserContext";
import Dropdown from "../Dropdown/Dropdown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Helmet } from "react-helmet";
import { apiDelete, apiGet, apiPut } from "../../../utils/ApiManager";

const deleteSuperadmin = process.env.REACT_APP_DELETE_ADMIN;
const fetchSuperadmin = process.env.REACT_APP_FETCH_ADMIN;
const recoverSuperadmin = process.env.REACT_APP_RECOVER_ADMIN;
const AdminData = process.env.REACT_APP_GET_ADMIN_DATA;
const handleAdminUpdate = process.env.REACT_APP_HANDLE_UPDATE;
const fetchDropdownRegisterAdmin =
  process.env.REACT_APP_DROPDOWN_REGISTER_ADMIN;

const AdminPage = () => {
  const navigateTo = useNavigate();
  const { requestConfirmation } = useUserContext();
  const { setLoading } = useLoaderContext();
  const { showToast } = useToastContext();
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    firstName: "",
    lastName: "",
    department: "",
    employeeId: "",
    role: "",
    phoneNo: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [dropdown, setDropdown] = useState({
    roles: [],
    departments: [],
  });

  const fetchDropdown = async () => {
    try {
      setLoading(true);
      const response = await apiGet(fetchDropdownRegisterAdmin);
      if (response?.success) {
        setDropdown({
          roles: response?.data?.roleList || [],
          departments: response?.data?.departmentList || [],
        });
      } else {
        showToast(response?.message || "Something went wrong !", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Something went wrong !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        setLoading(true);
        const response = await apiGet(fetchSuperadmin);
        if (response?.success) {
          setAdmins(response.data);
        } else {
          showToast(
            response?.message || "Failed to fetch admins",
            "error"
          );
        }
      } catch (error) {
        showToast(
          error?.response?.message ||
          error?.message ||
          "Failed to fetch admins",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAdmins();
    fetchDropdown();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // First Name
    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    // Last Name
    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    // Employee Id
    if (!formData.employeeId) {
      newErrors.employeeId = "Employee ID is required";
      isValid = false;
    } else if (formData.employeeId.length > 20) {
      newErrors.employeeId = "Employee ID cannot exceed 20 characters";
      isValid = false;
    }

    // Email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone Number
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phoneNo) {
      newErrors.phoneNo = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone number must be exactly 10 digits";
      isValid = false;
    }

    // Department
    if (!formData.department) {
      newErrors.department = "Please select a department";
      isValid = false;
    }

    // Role
    if (!formData.role) {
      newErrors.role = "Please select a job level";
      isValid = false;
    }

    if (formData.password || formData.confirmPassword) {
      // Password
      const passwordRegex =
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
      if (!formData.password) {
        newErrors.password = "Password is required";
        isValid = false;
      } else if (!passwordRegex.test(formData.password)) {
        newErrors.password =
          "Password must start with a capital letter, contain alphanumeric characters and at least one special character";
        isValid = false;
      }

      // Confirm Password
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Confirm password is required";
        isValid = false;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm(true);
    setIsFormValid(isValid);
  }, [formData]);

  const handleRegister = () => {
    navigateTo("/admin/register");
  };

  const deleteAdmin = async (userId) => {
    try {
      if (!userId) return;
      const result = await requestConfirmation("Want to delete this admin ?");
      if (!result) return;
      setLoading(true);
      const response = await apiDelete(`${deleteSuperadmin}/${userId}`);
      if (response?.success) {
        const updated = admins?.map((admin) =>
          admin?.userId === userId
            ? { ...admin, isDeleted: true, isActive: false }
            : admin
        );
        setAdmins([...updated]);
        showToast(
          response?.message || "Admin Deleted Successfully !",
          "success"
        );
      } else {
        showToast(
          response?.message || "Failed to delete admin !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete admin !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const recoverAdmin = async (userId) => {
    try {
      if (!userId) return;
      const result = await requestConfirmation("Want to recover this admin ?");
      if (!result) return;
      setLoading(true);
      const response = await apiPut(`${recoverSuperadmin}/${userId}`, {});
      if (response?.success) {
        const updated = admins?.map((admin) =>
          admin?.userId === userId
            ? { ...admin, isDeleted: false, isActive: true }
            : admin
        );
        setAdmins([...updated]);
        showToast(
          response?.message || "Admin Recovered Successfully !",
          "success"
        );
      } else {
        showToast(
          response?.message || "Failed to delete admin !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to delete admin !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getAdminData = async (userId) => {
    try {
      if (!userId) return;

      setLoading(true);
      const response = await apiGet(`${AdminData}/${userId}`);
      if (response?.success) {
        setFormData({
          ...response?.data,
          role: response?.data?.role?.role,
        });
        setOpen(true);
      } else {
        showToast(
          response?.message || "Failed to fetch admin data !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to fetch admin data !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (!isValid) return;

    const result = await requestConfirmation("Want to edit this admin ?");
    if (!result) return;
    try {
      setLoading(true);
      const response = await apiPut(`${handleAdminUpdate}/${formData.userId}`, formData);
      if (response?.success) {
        showToast(
          response?.message || "Admin edited successfully !",
          "success"
        );
        setAdmins((prevAdmins) =>
          prevAdmins.map((admin) =>
            admin.userId === formData.userId
              ? {
                ...admin,
                ...formData,
                role: formData.role,
              }
              : admin
          )
        );
        setFormData({
          userId: "",
          firstName: "",
          lastName: "",
          department: "",
          employeeId: "",
          role: "",
          phoneNo: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
        setOpen(false);
      } else {
        showToast(response?.message || "Failed to edit admin !", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to edit admin !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    validateForm();
  }, [formData]);

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Administration | DiNEST</title>
        <meta name="description" content="View admin dept. on DiNEST" />
      </Helmet>
      <Box className={styles.restaurantListContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Administration
        </Typography>

        <Button
          className={styles.registerButton}
          onClick={handleRegister}
          variant="contained"
        >
          Register Admin
        </Button>

        <Grid container spacing={4} className={styles.cardsContainer}>
          {Array.isArray(admins) && admins.length > 0 ? (
            admins.map((admin, index) => (
              <Grid
                // size={{ xs: 12, sm: 6, md: 4 }}
                key={index}
                className={styles.cardGrid}
              >
                <Card className={styles.adminCard}>
                  <div className={styles.boxImage}>
                    <CardMedia
                      component="img"
                      image={
                        admin.profileImage && admin.profileImage.trim() !== ""
                          ? admin.profileImage
                          : animatedAvatarUrl
                      }
                      alt={`${admin.firstName} ${admin.lastName}`}
                      className={styles.cardProfileImage}
                    />
                  </div>

                  <CardContent
                    className={styles.content}
                    sx={{
                      backgroundColor: admin?.isDeleted ? "#EE4B2B" : "#207d46",
                      color: "white",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >

                    <Tooltip
                      title={admin.firstName + " " + admin.lastName}
                      arrow
                    >
                      <Box display="flex" justifyContent="center">
                        <Typography
                          variant="h6"
                          sx={{
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: "90%",
                            cursor: "pointer",
                            textAlign: "center",
                          }}
                        >
                          {admin.firstName || "N/A"} {admin.lastName || "N/A"}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Typography sx={{ mb: 1 }} variant="body2">
                      ( {admin.employeeId || "N/A"} )
                    </Typography>

                    <Typography variant="body2">
                      {admin.role || "N/A"}
                    </Typography>
                    <Typography variant="body2"></Typography>
                    <Tooltip title={admin.email || "N/A"} arrow>
                      <Box display="flex" justifyContent="center">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: "90%",
                            textAlign: "center",
                            cursor: "pointer",
                          }}
                        >
                          {admin.email || "N/A"}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Box className={styles.iconBoxCard}>
                      <Tooltip title="Edit Admin" arrow>
                        <IconButton onClick={() => getAdminData(admin?.userId)}>
                          <EditIcon fontSize="small" sx={{ color: "white" }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={
                          admin?.isDeleted ? "Recover Admin" : "Delete Admin"
                        }
                        arrow
                      >
                        <IconButton
                          onClick={() =>
                            admin?.isDeleted
                              ? recoverAdmin(admin?.userId)
                              : deleteAdmin(admin?.userId)
                          }
                        >
                          {admin?.isDeleted ? (
                            <RestoreIcon
                              fontSize="small"
                              sx={{ color: "white" }}
                            />
                          ) : (
                            <DeleteIcon
                              fontSize="small"
                              sx={{ color: "white" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <h2 style={{ color: "red" }}>No admins data found !</h2>
          )}
        </Grid>
      </Box>

      <Dialog
        className={styles.editContainer}
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <div
          onClick={() => setOpen(false)}
          className={styles.backIconContainer}
        >
          <ArrowBackIcon className={styles.backIcon} />
        </div>

        <Typography variant="h5" className={styles.editContainerTitle}>
          Edit Admin
        </Typography>
        <form onSubmit={handleUpdate} className={styles.formScrollable}>
          <Grid container spacing={2}>
            {/* First Name */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="First Name"
                name="firstName"
                value={formData?.firstName}
                onChange={handleChange}
                variant="standard"
                fullWidth
                required
              />
              {errors?.firstName && (
                <p className={styles.error}>{errors?.firstName}</p>
              )}
            </Grid>

            {/* Last Name */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="Last Name"
                name="lastName"
                value={formData?.lastName}
                onChange={handleChange}
                variant="standard"
                fullWidth
                required
              />
              {errors?.lastName && (
                <p className={styles.error}>{errors?.lastName}</p>
              )}
            </Grid>

            {/* Email */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData?.email}
                onChange={handleChange}
                variant="standard"
                fullWidth
                required
              />
              {errors?.email && <p className={styles.error}>{errors?.email}</p>}
            </Grid>

            {/* Phone Number */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="Phone Number"
                name="phoneNo"
                value={formData?.phoneNo}
                onChange={handleChange}
                variant="standard"
                fullWidth
                required
              />
              {errors?.phoneNo && (
                <p className={styles.error}>{errors?.phoneNo}</p>
              )}
            </Grid>

            {/* Department */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <Dropdown
                label="Department"
                options={dropdown.departments}
                onChange={handleChange}
                name="department"
                value={formData?.department}
              />
              {errors?.department && (
                <p className={styles.error}>{errors?.department}</p>
              )}
            </Grid>

            {/* Job Role */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <Dropdown
                label="Job Level"
                options={dropdown.roles}
                onChange={handleChange}
                name="role"
                value={formData?.role}
              />
              {errors?.role && <p className={styles.error}>{errors?.role}</p>}
            </Grid>

            {/* Employee ID */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="Employee ID"
                name="employeeId"
                value={formData?.employeeId}
                onChange={handleChange}
                variant="standard"
                fullWidth
              />
              {errors?.employeeId && (
                <p className={styles.error}>{errors?.employeeId}</p>
              )}
            </Grid>

            {/* Password */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData?.password}
                onChange={handleChange}
                variant="standard"
                fullWidth
              />
              {errors?.password && (
                <p className={styles.error}>{errors?.password}</p>
              )}
            </Grid>

            {/* Confirm Password */}
            <Grid size={{ xs: 12 }} className={styles.inputGroup}>
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                variant="standard"
                fullWidth
              />
              {errors?.confirmPassword && (
                <p className={styles.error}>{errors?.confirmPassword}</p>
              )}
            </Grid>

            {/* Submit & Cancel Buttons */}
            <Grid size={{ xs: 12 }} className={styles.buttonContainer}>
              <Button
                disabled={!isFormValid}
                type="submit"
                variant="contained"
                fullWidth
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </form>
      </Dialog>
    </Box >
  );
};

export default AdminPage;
